import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';
import errorImg from '@assets/img/error_img.png';
import Cookies from 'js-cookie';
import axios from 'axios';
import { useSetRecoilState } from 'recoil';
import storeInfo from '../../recoil/storeInfo/atom';
import { logoutRequest } from '@apis/signIn';
import { clearDemoSession } from '@services/demo/demoAccount';

const ErrorPage = () => {
  const error = useRouteError();
  const navigation = useNavigate();
  const setCurrentStoreInfo = useSetRecoilState(storeInfo);
  const [count, setCount] = useState(3);
  const leavingRef = useRef(false);

  const leaveToSignIn = useCallback(async () => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    try {
      await logoutRequest();
    } catch {
      // 세션이 이미 없거나 로그아웃 API가 실패해도 로그인 화면으로 보낸다.
    }
    clearDemoSession();
    Cookies.remove('smartlab_authority', { path: '/' });
    Cookies.remove('userType', { path: '/' });
    setCurrentStoreInfo({
      currentStore: '',
      currentAuthority: '',
      currentStoreId: undefined,
      storeLists: [],
    });
    navigation('/signin', { replace: true });
  }, [navigation, setCurrentStoreInfo]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCount((prevCount) => {
        if (prevCount <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prevCount - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (count !== 0) return;
    if (isRouteErrorResponse(error) && error.status === 404) {
      if (leavingRef.current) return;
      leavingRef.current = true;
      navigation(-1);
      return;
    }
    void leaveToSignIn();
  }, [count, error, leaveToSignIn, navigation]);

  const loginButton = (
    <button type="button" className="error_login_btn" onClick={() => void leaveToSignIn()}>
      로그인으로 이동
    </button>
  );

  let errorStatus: number | string | undefined = '';

  if (isRouteErrorResponse(error)) {
    errorStatus = error.status;
  } else if (axios.isAxiosError(error) && error.response) {
    errorStatus = error.response.status;
  } else {
    errorStatus = '';
  }

  if (errorStatus === 404) {
    return (
      <div id="error_container">
        <div className="text_wrapper">
          <div className="error_status">404</div>
          <h1 className="error_title">원하시는 페이지를 찾을 수 없습니다.</h1>
          <h2 className="error_sub_text">
            찾으시는 페이지의 주소가 잘못 입력되었거나,
            <br /> 주소의 변경 혹은 삭제로 인해 사용하실 수 없습니다.
          </h2>
          <h3 className="error_back_text">{`${count}초 후 이전 페이지로 돌아갑니다.`}</h3>
          {loginButton}
        </div>
        <img src={errorImg} alt="에러 이미지" className="error_img" />
      </div>
    );
  } else if (errorStatus === 401) {
    return (
      <div id="error_container">
        <div className="text_wrapper">
          <div className="error_status">401</div>
          <h1 className="error_title">인증되지 않은 사용자 입니다.</h1>
          <h2 className="error_sub_text">
            로그인 정보가 유효하지 않거나,
            <br /> 로그인 유효 시간이 경과하였습니다.
          </h2>
          <h3 className="error_back_text">{`${count}초 후 로그인 페이지로 돌아갑니다.`}</h3>
          {loginButton}
        </div>
        <img src={errorImg} alt="에러 이미지" className="error_img" />
      </div>
    );
  } else if (errorStatus === 403 || errorStatus === 400) {
    return (
      <div id="error_container">
        <div className="text_wrapper">
          <div className="error_status">403</div>
          <h1 className="error_title">권한이 없어 요청이 거부되었습니다.</h1>
          <h2 className="error_sub_text">
            관리자에게 권한을 요청하거나,
            <br /> 해당 기능의 권한 여부를 확인해주세요.
          </h2>
          <h3 className="error_back_text">{`${count}초 후 로그인 페이지로 돌아갑니다.`}</h3>
          {loginButton}
        </div>
        <img src={errorImg} alt="에러 이미지" className="error_img" />
      </div>
    );
  } else {
    return (
      <div id="error_container">
        <div className="text_wrapper">
          <div className="error_status">ERROR</div>
          <h1 className="error_title">알 수 없는 오류입니다.</h1>
          <h2 className="error_sub_text">
            로그아웃 후 재시도 해주세요
            <br /> 해당 오류가 계속되면 관리자에게 문의해주세요.
          </h2>
          <h3 className="error_back_text">{`${count}초 후 로그인 페이지로 돌아갑니다.`}</h3>
          {loginButton}
        </div>
        <img src={errorImg} alt="에러 이미지" className="error_img" />
      </div>
    );
  }
};

export default ErrorPage;
